import { useForm, type SubmitHandler } from "react-hook-form"

import useFormLocalStorage from "@/hooks/use-form-local-storage"
import { APP_VERSION } from "@/constants/general"
import Form from "@/components/forms/reusables/form"
import useFormStates from "@/hooks/use-form-states"
import RHFRichText from "@/components/form-fields/rhf-rich-text"
import type {
    ApplicationInstruction,
    ApplicationInstructionFormInput,
} from "@/types"
import {
    useCreateApplicationInstruction,
    useUpdateApplicationInstruction,
} from "@/hooks/api/use-application-instruction"

const ApplicationInstructionForm = ({
    code,
    applicationInstruction,
    closeCallback,
} : {
    code: string,
    applicationInstruction?: ApplicationInstruction,
    closeCallback?: () => void,
}) => {

    const formDataId = `applicationInstructionForm-${applicationInstruction?.code}-v${APP_VERSION}`

    const initialValues = {
        code: applicationInstruction ? applicationInstruction.description : code,
        description: applicationInstruction ? applicationInstruction.description : '',
    }
    
    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
        watch,
    } = useForm({
        defaultValues: { ...initialValues },
    })

    const [isLoadedFromLocalStorage, handleFormReset] = useFormLocalStorage({
        control,
        formDataId,
        reset,
        initialValues
    })

    const {
        resetWithVersion,
        requestInProgress,
        setRequestInProgress,
    } = useFormStates(handleFormReset)

    const mutationCreate = useCreateApplicationInstruction({
        setRequestInProgress,
        formDataId,
        onSuccessCallback: closeCallback,
    })

    const mutationUpdate = useUpdateApplicationInstruction(
        code,
        {
            setRequestInProgress,
            formDataId,
            onSuccessCallback: closeCallback,
        },
    )

    const onSubmit: SubmitHandler<ApplicationInstructionFormInput> = data => {
        setRequestInProgress(true)
        if (applicationInstruction?.code) {
            mutationUpdate.mutate(data)
        } else {
            mutationCreate.mutate(data)
        }
    }

    const description = watch('description')

    return (
        <Form
            onSubmit={handleSubmit(onSubmit)}
            heading={applicationInstruction ? 'Upravit obsah' : 'Vytvořit nápovědu'}
            closeCallback={closeCallback}
            isLoadedFromLocalStorage={isLoadedFromLocalStorage}
            isDirty={isDirty}
            resetWithVersion={resetWithVersion}
            requestInProgress={requestInProgress}
            buttonText={applicationInstruction ? 'Upravit' : 'Vytvořit nápovědu'}
            disableSave={!description}
        >
            <RHFRichText
                name="description"
                control={control}
                label="Obsah *"
                multipleRows
            />
        </Form>
    )
}

export default ApplicationInstructionForm
